/**
 * État civil du compte : nom, nom affiché, langue, fuseau.
 *
 * C'est l'écran qui manquait pour corriger le nom d'un compte créé en ligne de
 * commande — le premier administrateur, notamment.
 */
import { useEffect, useId, useState, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  useUpdateAccount,
  type Account,
  type AccountUpdate,
} from "@/features/account/api/useAccount";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { SUPPORTED_LOCALES } from "@/shared/i18n";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card, DefinitionList } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";

import { timeZones } from "@/features/account/lib/timeZones";

interface IdentityFormProps {
  account: Account;
  /** Faux tant que l'adresse email n'est pas confirmée (docs/06 §4). */
  canEdit: boolean;
  roles: readonly string[];
  permissionCount: number;
}

export function IdentityForm({ account, canEdit, roles, permissionCount }: IdentityFormProps) {
  const { t } = useTranslation("account");
  const update = useUpdateAccount();
  const errorMessage = useApiErrorMessage();
  const selectId = useId();

  const [firstName, setFirstName] = useState(account.first_name);
  const [lastName, setLastName] = useState(account.last_name);
  const [displayName, setDisplayName] = useState(account.display_name);
  const [locale, setLocale] = useState(account.locale);
  const [timezone, setTimezone] = useState(account.timezone);

  // Le serveur normalise (blancs rognés, nom affiché retombé sur « prénom nom ») :
  // on réaligne les champs sur ce qui a été **enregistré**, sinon l'écran
  // continuerait d'afficher la saisie brute.
  useEffect(() => {
    setFirstName(account.first_name);
    setLastName(account.last_name);
    setDisplayName(account.display_name);
    setLocale(account.locale);
    setTimezone(account.timezone);
  }, [account]);

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const body: AccountUpdate = {};
    if (firstName !== account.first_name) {
      body.first_name = firstName;
    }
    if (lastName !== account.last_name) {
      body.last_name = lastName;
    }
    if (displayName !== account.display_name) {
      // Vidé par l'utilisateur ⇒ effacement explicite, et l'affichage retombe
      // sur « prénom nom ». Envoyer `""` demanderait au serveur de deviner.
      body.display_name = displayName.trim() === "" ? null : displayName;
    }
    if (locale !== account.locale) {
      body.locale = locale;
    }
    if (timezone !== account.timezone) {
      body.timezone = timezone;
    }
    if (Object.keys(body).length === 0) {
      return;
    }
    update.mutate(body);
  };

  return (
    <Card title={t("identity.heading")}>
      <form onSubmit={handleSubmit} aria-label={t("identity.heading")} className="space-y-4">
        <DefinitionList
          items={[
            { label: t("identity.email"), value: account.email },
            { label: t("identity.roles"), value: roles.join(", ") },
            { label: t("identity.permissionCount"), value: String(permissionCount) },
          ]}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("identity.firstName")}
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
            }}
            disabled={!canEdit}
            required
          />
          <Field
            label={t("identity.lastName")}
            value={lastName}
            onChange={(event) => {
              setLastName(event.target.value);
            }}
            disabled={!canEdit}
            required
          />
        </div>

        <Field
          label={t("identity.displayName")}
          hint={t("identity.displayNameHint")}
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
          disabled={!canEdit}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor={`${selectId}-locale`} className="block text-sm font-medium">
              {t("identity.locale")}
            </label>
            <select
              id={`${selectId}-locale`}
              value={locale}
              onChange={(event) => {
                setLocale(event.target.value);
              }}
              disabled={!canEdit}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
            >
              {SUPPORTED_LOCALES.map((code) => (
                <option key={code} value={code}>
                  {t(`languages.${code}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor={`${selectId}-timezone`} className="block text-sm font-medium">
              {t("identity.timezone")}
            </label>
            <select
              id={`${selectId}-timezone`}
              value={timezone}
              onChange={(event) => {
                setTimezone(event.target.value);
              }}
              disabled={!canEdit}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
            >
              {timeZones(account.timezone).map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>
        </div>

        {update.isError && <Alert tone="error">{errorMessage(update.error)}</Alert>}
        {update.isSuccess && <Alert tone="success">{t("saved")}</Alert>}

        <Button type="submit" disabled={!canEdit || update.isPending}>
          {update.isPending ? t("saving") : t("save")}
        </Button>
      </form>
    </Card>
  );
}
